import React, { useEffect, useState } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';

import interceptConnect from 'helpers/interceptConnect';
import formatNumber from 'helpers/formatNumber';
import useOffset from 'hooks/useOffset';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import { RadioGroupTabsWrapper } from 'components/data-containers/BrowseEntities/BrowseControls/BrowseControls';
import RadioGroup from 'components/base/RadioGroup/RadioGroup';
import Body from 'components/data-visualization/Table/Body';
import Dropdown from 'components/base/Dropdown/Dropdown';
import Box from 'components/elements/Box';
import SkeletonLoader from './SkeletonLoader';
import Pagination from 'components/data-visualization/Table/Pagination';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';

import { StyledEntityItem } from 'components/data-containers/BrowseEntities/TableRow/TableRow.style';
import { StyledCell, StyledRow, StyledTable, Text } from './styles';

interface IOuterOptions {
  isLargeScreen: boolean;
  title: string;
}

interface ITopPerformingArtistsOnSocialProps extends IOuterOptions {
  fetchSocials: any;
  socialsData: any;
}

export const initialSocials = [
  { label: 'Spotify', value: 'Spotify' },
  { label: 'Twitter', value: 'Twitter' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Instagram', value: 'Instagram' },
];

const dataSources = (): any => {
  return {
    fetchSocials: ({
      limit,
      offset,
      platformFilter,
      sortBy,
      sortOrder,
    }: {
      limit: number;
      offset: number;
      platformFilter: string[];
      sortBy: string;
      sortOrder: 'asc' | 'desc';
    }): any => {
      return {
        socialsData: {
          url: 'artistsOnSocial',
          interceptionData: {
            limit,
            offset,
            platformFilter,
            sortBy,
            sortOrder,
          },
          comparison: platformFilter,
        },
      };
    },
  };
};

const TopPerformingArtistsOnSocial: React.FC<ITopPerformingArtistsOnSocialProps> =
  React.memo((props) => {
    const {
      fetchSocials,
      isLargeScreen,
      socialsData = PromiseState.create(),
      title,
    } = props;
    const [selectedSocial, selectSocial] = useState(initialSocials[0]);

    // `useOffset` concats or invalidates the entity data when subsequent
    // API calls are made. For instance, when the user clicks "Show 10 more"
    // this hook will make sure the entities are added to the previous ones.
    // When we switch the sort metric, order, or filters — invalidation makes sure
    // the previous entities are flushed.
    const { offsetState, setOffset, setEntities, setInvalidate } = useOffset({
      limit: 10,
      increment: 10,
      entitiesKey: 'data',
    });

    const { limit, offset, value } = offsetState;
    const entities = delve(value, 'data');
    const totalLimit = delve(value, 'totalLimit');

    useEffect(() => {
      if (socialsData?.value) {
        setEntities(socialsData?.value);
      }
    }, [socialsData, setEntities]);

    /*
     * When an interaction took place that will trigger a new API call, we want to
     * clear the entities in state. This is because the API should compute that and
     * the entities in state are invalid with the upcoming state.
     */
    useEffect(() => {
      // `setInvalidate` flushes the entities when the next batch of entities arrive.
      // Happens at the next `setEntities` so we don't see a skeleton loader in between.
      setInvalidate();
    }, [setInvalidate, selectedSocial.value]);

    useEffect(() => {
      fetchSocials({
        limit,
        offset,
        platformFilter: [selectedSocial?.value],
        sortBy: 'followers',
        sortOrder: 'desc',
      });
    }, [selectedSocial, offset, limit]);

    return (
      <>
        {socialsData?.pending && !entities ? (
          <SkeletonLoader
            initialTab={selectedSocial}
            isLargeScreen={isLargeScreen}
            title={title}
            toggleOptions={initialSocials}
          />
        ) : (
          <GenericComponent
            title={title}
            loading={socialsData?.pending}
            showToolbarDropdown={false}
            allowedFilters={[]}
            isWide
          >
            <ErrorBoundary
              error={
                socialsData?.rejected ? new Error(socialsData?.reason) : null
              }
              errorInfo={socialsData?.reason}
            >
              <>
                {isLargeScreen ? (
                  <RadioGroupTabsWrapper>
                    <RadioGroup
                      name="top-lists-type"
                      options={initialSocials}
                      selectedOption={selectedSocial?.value}
                      onChange={(item: {
                        label: string;
                        value: string;
                      }): void => selectSocial(item)}
                    />
                  </RadioGroupTabsWrapper>
                ) : (
                  <Box>
                    <Dropdown
                      items={initialSocials}
                      selectedItem={selectedSocial}
                      onChange={(item: { label: string; value: any }): void =>
                        selectSocial(item)
                      }
                      isToggleCaret={true}
                      isRoundedDropdown="all"
                    />
                  </Box>
                )}
                <AnimatedLoadingWrapper loading={socialsData?.pending}>
                  <StyledTable>
                    <Body>
                      {entities &&
                        entities.length > 0 &&
                        entities.map(
                          (
                            item: { entity: any; followers: number },
                            id: number
                          ) => (
                            <StyledRow key={id}>
                              <StyledCell className="artist">
                                <StyledEntityItem
                                  entityId={item?.entity?.id}
                                  entityType={item?.entity?.type}
                                  displayName={item?.entity?.displayName}
                                  primaryArtist={item?.entity?.primaryArtist}
                                  thumbnailUrl={item?.entity?.thumbnailUrl}
                                  releaseDate={item?.entity?.releaseDate}
                                  isGroup={item?.entity?.is_group}
                                  parentEntityId={item?.entity?.parent_group_id}
                                  groupChildCount={
                                    item.entity.group_child_count
                                  }
                                  entityData={item?.entity}
                                  releaseCount={
                                    item?.entity?.grouped_album_count
                                  }
                                  artistCount={item?.entity?.artist_count}
                                  trackCount={item?.entity?.grouped_track_count}
                                  preserveSearch={true}
                                  isHighlighted={false}
                                  isLink={true}
                                  releaseType={item.entity?.album?.productType}
                                  isrc_list={item?.entity?.isrc_list}
                                />
                              </StyledCell>
                              <StyledCell className="metric">
                                <Text>{`${formatNumber(
                                  item?.followers,
                                  'number',
                                  true
                                )} Followers`}</Text>
                              </StyledCell>
                            </StyledRow>
                          )
                        )}
                    </Body>
                  </StyledTable>

                  {entities && entities.length > 0 && (
                    <Pagination
                      current={entities.length}
                      total={totalLimit}
                      offset={10}
                      hasMoreResults={entities.length < totalLimit}
                      onClick={setOffset}
                      isWideButtonWithTotal
                    />
                  )}
                </AnimatedLoadingWrapper>
              </>
            </ErrorBoundary>
          </GenericComponent>
        )}
      </>
    );
  });

export default interceptConnect<IOuterOptions>(dataSources as any)(
  TopPerformingArtistsOnSocial as any
);
